import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

interface User {
  id: number;
  username: string;
  email: string;
  role: 'admin' | 'user';
  credits: number;
  isVerified: boolean;
  twoFactorEnabled: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string, twoFactorCode?: string) => Promise<void>;
  logout: () => void;
  register: (email: string, password: string, confirmPassword: string, firstName: string, lastName: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  enableTwoFactor: () => Promise<string>;
  verifyTwoFactor: (code: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  requestOtp: (email: string, type: string) => Promise<{ success: boolean; message: string; expiresAt?: string }>;
  verifyOtp: (email: string, code: string, type: string) => Promise<{ success: boolean; message: string }>;
  loginWithOtp: (email: string, code: string) => Promise<void>;
  googleAuth: (idToken: string) => Promise<{ isNewUser: boolean }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL + '/api';
axios.defaults.withCredentials = true;

// Add token to requests if available
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const refreshUser = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/auth/profile');
      const userData = response.data;
      
      // Store userId in localStorage for API calls
      localStorage.setItem('userId', userData.id.toString());
      
      // Map backend UserDto to frontend User interface
      const mappedUser: User = {
        id: userData.id,
        username: userData.username,
        email: userData.email,
        role: userData.roles?.includes('Admin') ? 'admin' : 'user',
        credits: userData.credits,
        isVerified: userData.isEmailVerified,
        twoFactorEnabled: userData.isTwoFactorEnabled
      };
      
      setUser(mappedUser);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      refreshUser();
    } else {
      setIsLoading(false);
    }
  }, [refreshUser]);

  const login = async (username: string, password: string, twoFactorCode?: string) => {
    try {
      const response = await axios.post('/auth/login', {
        username,
        password,
        twoFactorCode
      });
      
      const { token, user: userData } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('userId', userData.id.toString()); // Store userId
      
      // Map backend UserDto to frontend User interface
      const mappedUser: User = {
        id: userData.id,
        username: userData.username,
        email: userData.email,
        role: userData.roles?.includes('Admin') ? 'admin' : 'user',
        credits: userData.credits,
        isVerified: userData.isEmailVerified,
        twoFactorEnabled: userData.isTwoFactorEnabled
      };
      
      setUser(mappedUser);
      setIsAuthenticated(true);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId'); // Remove userId on logout
    setUser(null);
    setIsAuthenticated(false);
    // Clear all cached data on logout
    queryClient.clear();
  };

  const register = async (email: string, password: string, confirmPassword: string, firstName: string, lastName: string) => {
    try {
      await axios.post('/auth/register', { 
        email, 
        password, 
        confirmPassword, 
        firstName, 
        lastName 
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  };

  const verifyEmail = async (token: string) => {
    try {
      await axios.post('/auth/verify-email', { token });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Email verification failed');
    }
  };

  const enableTwoFactor = async (): Promise<string> => {
    try {
      const response = await axios.post('/auth/enable-2fa');
      return response.data.qrCode;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to enable 2FA');
    }
  };

  const verifyTwoFactor = async (code: string) => {
    try {
      await axios.post('/auth/verify-2fa', { code });
      await refreshUser();
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '2FA verification failed');
    }
  };

  const requestOtp = async (email: string, type: string) => {
    try {
      const response = await axios.post('/auth/request-otp', { email, type });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to send OTP');
    }
  };

  const verifyOtp = async (email: string, code: string, type: string) => {
    try {
      const response = await axios.post('/auth/verify-otp', { email, code, type });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'OTP verification failed');
    }
  };

  const loginWithOtp = async (email: string, code: string) => {
    try {
      const response = await axios.post('/auth/login-otp', { email, code });
      const { token, user: userData } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('userId', userData.id.toString()); // Store userId
      
      // Map backend UserDto to frontend User interface
      const mappedUser: User = {
        id: userData.id,
        username: userData.username,
        email: userData.email,
        role: userData.roles?.includes('Admin') ? 'admin' : 'user',
        credits: userData.credits,
        isVerified: userData.isEmailVerified,
        twoFactorEnabled: userData.isTwoFactorEnabled
      };
      
      setUser(mappedUser);
      setIsAuthenticated(true);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'OTP login failed');
    }
  };

  const googleAuth = async (idToken: string) => {
    try {
      const response = await axios.post('/auth/google-auth', { idToken });
      const { token, user: userData, isNewUser } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('userId', userData.id.toString()); // Store userId
      setUser(userData);
      setIsAuthenticated(true);
      return { isNewUser };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Google authentication failed');
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    register,
    verifyEmail,
    enableTwoFactor,
    verifyTwoFactor,
    refreshUser,
    requestOtp,
    verifyOtp,
    loginWithOtp,
    googleAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}